import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, X, Users, CheckSquare, Square, Download, Send, Mail, KeyRound, UserPlus, Shuffle, Copy, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';
import AnimatedSearchBar from '../components/AnimatedSearchBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Modal de Conceder Acesso ao Portal do Cliente
  const [showConcederModal, setShowConcederModal] = useState(false);
  const [concederClient, setConcederClient] = useState(null);
  const [concederLoading, setConcederLoading] = useState(false);
  const [concederForm, setConcederForm] = useState({
    email: '', metodo: 'auto_email', senha_personalizada: '', showPassword: false,
  });
  const [concederResult, setConcederResult] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    cnpj_cpf: '',
    telefone: '',
    whatsapp: '',
    email: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    numero_boleto: '',
    dados_nota_fiscal: '',
    situacao_pagamento: 'em dia'
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Cliente cadastrado com sucesso!');
      }
      fetchClients();
      closeModal();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const openNewModal = () => {
    setEditingClient(null);
    setFormData({
      nome: '',
      cnpj_cpf: '',
      telefone: '',
      whatsapp: '',
      email: '',
      endereco: '',
      cidade: '',
      estado: '',
      cep: '',
      numero_boleto: '',
      dados_nota_fiscal: '',
      situacao_pagamento: 'em dia'
    });
    setShowModal(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      nome: client.nome || '',
      cnpj_cpf: client.cnpj_cpf || '',
      telefone: client.telefone || '',
      whatsapp: client.whatsapp || '',
      email: client.email || '',
      endereco: client.endereco || '',
      cidade: client.cidade || '',
      estado: client.estado || '',
      cep: client.cep || '',
      numero_boleto: client.numero_boleto || '',
      dados_nota_fiscal: client.dados_nota_fiscal || '',
      situacao_pagamento: client.situacao_pagamento || 'em dia'
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
  };

  const handleConcederAcessoCliente = (client) => {
    setConcederClient(client);
    setConcederForm({
      email: client.email || '',
      metodo: 'auto_email',
      senha_personalizada: '',
      showPassword: false,
    });
    setConcederResult(null);
    setShowConcederModal(true);
  };

  const closeConcederModal = () => {
    setShowConcederModal(false);
    setConcederClient(null);
    setConcederResult(null);
    setConcederForm({ email: '', metodo: 'auto_email', senha_personalizada: '', showPassword: false });
  };

  const submitConcederAcesso = async () => {
    if (!concederClient) return;
    const email = (concederForm.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('E-mail inválido'); return; }
    if (concederForm.metodo === 'manual') {
      if (!concederForm.senha_personalizada || concederForm.senha_personalizada.length < 4) {
        toast.error('Senha deve ter ao menos 4 caracteres'); return;
      }
    }
    setConcederLoading(true);
    try {
      const payload = {
        email, name: concederClient.nome, role: 'cliente', client_id: concederClient.id,
        send_email: concederForm.metodo === 'auto_email',
        custom_password: concederForm.metodo === 'manual' ? concederForm.senha_personalizada : null,
        force_password_change: concederForm.metodo !== 'manual',
      };
      const resp = await axios.post(`${API}/auth/users/invite`, payload);
      const data = resp.data || {};
      setConcederResult({
        email: data.email,
        senha: data.senha_temporaria,
        email_enviado: data.email_enviado,
        action: data.action,
        erro_email: data.erro_email,
        must_change_password: data.must_change_password,
        metodo: concederForm.metodo,
      });
      // Salva email no cliente
      try {
        await axios.put(`${API}/clients/${concederClient.id}`, { ...concederClient, email });
      } catch { /* ignore */ }
      toast.success(`Acesso ${data.action} para ${concederClient.nome}`);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao conceder acesso');
    } finally {
      setConcederLoading(false);
    }
  };

  const copiarCredenciaisCliente = async (formato = 'completo') => {
    if (!concederResult) return;
    const loginUrl = `${window.location.origin}/login`;
    let texto = '';
    if (formato === 'senha') {
      texto = concederResult.senha;
    } else {
      texto = `Acesso ao Portal do Cliente — Distribuidora Santa Ana\n\nE-mail: ${concederResult.email}\nSenha: ${concederResult.senha}\nLink: ${loginUrl}`;
      if (concederResult.must_change_password) {
        texto += `\n\nNo primeiro login será solicitado para criar uma nova senha pessoal.`;
      }
    }
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Copiado!');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este cliente?')) {
      try {
        await axios.delete(`${API}/clients/${id}`);
        toast.success('Cliente deletado com sucesso!');
        fetchClients();
      } catch (error) {
        toast.error('Erro ao deletar cliente');
      }
    }
  };

  const filteredClients = clients.filter(client => 
    client.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.cnpj_cpf?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.cidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.estado?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  // Funções de seleção múltipla
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(paginatedClients.map(c => c.id));
      setSelectAll(true);
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(i => i !== id));
      setSelectAll(false);
    } else {
      setSelectedIds(prev => [...prev, id]);
      if (selectedIds.length + 1 === paginatedClients.length) setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return toast.error('Selecione pelo menos um cliente');
    if (!window.confirm(`Excluir ${selectedIds.length} cliente(s)?`)) return;

    try {
      for (const id of selectedIds) await axios.delete(`${API}/clients/${id}`);
      toast.success(`${selectedIds.length} cliente(s) excluído(s)!`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchClients();
    } catch (error) {
      toast.error('Erro ao excluir clientes');
    }
  };

  const handleExportCSV = () => {
    if (selectedIds.length === 0) return toast.error('Selecione pelo menos um cliente');
    const selected = clients.filter(c => selectedIds.includes(c.id));
    const headers = ['Nome', 'CNPJ/CPF', 'Cidade', 'Estado', 'WhatsApp', 'Email', 'Situação'];
    const rows = selected.map(c => [c.nome, c.cnpj_cpf || '', c.cidade || '', c.estado || '', c.whatsapp || '', c.email || '', c.situacao_pagamento || '']);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'clientes_exportados.csv';
    link.click();
    toast.success('Arquivo exportado!');
  };

  const handleBulkWhatsApp = () => {
    if (selectedIds.length === 0) return toast.error('Selecione pelo menos um cliente');
    const selected = clients.filter(c => selectedIds.includes(c.id) && c.whatsapp);
    if (selected.length === 0) return toast.error('Nenhum cliente selecionado possui WhatsApp');
    
    const message = encodeURIComponent('Olá! Tudo bem?');
    selected.forEach((c, i) => {
      const phone = c.whatsapp.replace(/\D/g, '');
      if (phone) {
        setTimeout(() => {
          window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
        }, i * 500);
      }
    });
    toast.success(`Abrindo WhatsApp para ${selected.length} cliente(s)`);
  };

  const handleBulkEmail = () => {
    if (selectedIds.length === 0) return toast.error('Selecione pelo menos um cliente');
    const selected = clients.filter(c => selectedIds.includes(c.id) && c.email);
    if (selected.length === 0) return toast.error('Nenhum cliente selecionado possui email');
    
    const emails = selected.map(c => c.email).join(',');
    window.open(`mailto:${emails}`, '_blank');
    toast.success(`Abrindo email para ${selected.length} cliente(s)`);
  };

  const clearSelection = () => { setSelectedIds([]); setSelectAll(false); };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-heading font-bold text-foreground">Clientes</h2>
          <p className="text-muted-foreground mt-1">{clients.length} clientes cadastrados</p>
        </div>
        <button onClick={openNewModal} data-testid="add-client-btn" className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Novo Cliente
        </button>
      </div>

      {/* Barra de Ações em Massa */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary">{selectedIds.length} cliente(s) selecionado(s)</span>
            <button onClick={clearSelection} className="text-sm text-muted-foreground hover:text-foreground">Limpar</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleBulkWhatsApp} className="btn-secondary flex items-center gap-2 text-sm py-2" title="Enviar WhatsApp">
              <Send className="w-4 h-4" />
              WhatsApp
            </button>
            <button onClick={handleBulkEmail} className="btn-secondary flex items-center gap-2 text-sm py-2" title="Enviar Email">
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button onClick={handleExportCSV} className="btn-secondary flex items-center gap-2 text-sm py-2">
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
            <button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              <Trash2 className="w-4 h-4" />
              Excluir
            </button>
          </div>
        </div>
      )}

      {/* Barra de Busca — Animated */}
      <AnimatedSearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Buscar por nome, CNPJ/CPF, cidade ou estado..."
        onClearAll={() => setSearchTerm('')}
        testIdPrefix="clients"
        hideFilters
      />

      {/* Tabela */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-3 text-left text-sm font-semibold w-12">
                  <button onClick={toggleSelectAll} className="p-1 hover:bg-accent rounded">
                    {selectAll ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-muted-foreground" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">CNPJ/CPF</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Cidade/UF</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">WhatsApp</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Situação</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedClients.map((client) => (
                <tr key={client.id} className={`hover:bg-accent/50 transition-colors ${selectedIds.includes(client.id) ? 'bg-primary/5' : ''}`}>
                  <td className="px-3 py-3 text-sm">
                    <button onClick={() => toggleSelectOne(client.id)} className="p-1 hover:bg-accent rounded">
                      {selectedIds.includes(client.id) ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-muted-foreground" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{client.nome}</td>
                  <td className="px-4 py-3 text-sm">{client.cnpj_cpf}</td>
                  <td className="px-4 py-3 text-sm">{client.cidade}/{client.estado}</td>
                  <td className="px-4 py-3 text-sm">{client.whatsapp}</td>
                  <td className="px-4 py-3 text-sm text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      client.situacao_pagamento === 'em dia' ? 'bg-green-100 text-green-800' :
                      client.situacao_pagamento === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {client.situacao_pagamento}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <button onClick={() => handleConcederAcessoCliente(client)} className="text-blue-600 hover:text-blue-800 mr-3" title="Conceder acesso ao Portal do Cliente" data-testid={`btn-conceder-acesso-cliente-${client.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEditModal(client)} className="text-primary hover:text-primary/80 mr-3" title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(client.id)} className="text-red-600 hover:text-red-800" title="Deletar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedClients.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                    {searchTerm ? 'Nenhum cliente encontrado com esse termo' : 'Nenhum cliente cadastrado'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-sm text-muted-foreground">
              Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredClients.length)} de {filteredClients.length}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-accent disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-2 text-sm">{currentPage} / {totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-accent disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Edição/Criação */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {editingClient ? 'Editar Cliente' : 'Novo Cliente'}
              </h3>
              <button onClick={closeModal} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome *</label>
                  <input type="text" value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} className="input-field" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CNPJ/CPF</label>
                  <input type="text" value={formData.cnpj_cpf} onChange={(e) => setFormData({...formData, cnpj_cpf: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Telefone</label>
                  <input type="text" value={formData.telefone} onChange={(e) => setFormData({...formData, telefone: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">WhatsApp</label>
                  <input type="text" value={formData.whatsapp} onChange={(e) => setFormData({...formData, whatsapp: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Situação</label>
                  <select value={formData.situacao_pagamento} onChange={(e) => setFormData({...formData, situacao_pagamento: e.target.value})} className="input-field">
                    <option value="em dia">Em dia</option>
                    <option value="pendente">Pendente</option>
                    <option value="atrasado">Atrasado</option>
                  </select>
                </div>
                
                {/* Endereço */}
                <div className="lg:col-span-3 border-t border-border pt-4 mt-2">
                  <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Endereço</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium mb-2">Endereço</label>
                      <input type="text" value={formData.endereco} onChange={(e) => setFormData({...formData, endereco: e.target.value})} className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Cidade</label>
                      <input type="text" value={formData.cidade} onChange={(e) => setFormData({...formData, cidade: e.target.value})} className="input-field" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium mb-2">UF</label>
                        <input type="text" value={formData.estado} onChange={(e) => setFormData({...formData, estado: e.target.value})} className="input-field" maxLength={2} placeholder="SP" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">CEP</label>
                        <input type="text" value={formData.cep} onChange={(e) => setFormData({...formData, cep: e.target.value})} className="input-field" placeholder="00000-000" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dados Fiscais */}
                <div className="lg:col-span-3 border-t border-border pt-4 mt-2">
                  <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Dados Fiscais</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nº Boleto</label>
                      <input type="text" value={formData.numero_boleto} onChange={(e) => setFormData({...formData, numero_boleto: e.target.value})} className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Dados Nota Fiscal</label>
                      <input type="text" value={formData.dados_nota_fiscal} onChange={(e) => setFormData({...formData, dados_nota_fiscal: e.target.value})} className="input-field" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingClient ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Conceder Acesso ao Portal do Cliente */}
      {showConcederModal && concederClient && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="conceder-acesso-cliente-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-emerald-600/10 to-teal-600/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-600 rounded-lg">
                  <KeyRound className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">Conceder Acesso ao Portal do Cliente</h2>
                  <p className="text-xs text-muted-foreground">Cliente: <span className="font-semibold">{concederClient.nome}</span></p>
                </div>
              </div>
              <button onClick={closeConcederModal} className="p-1 hover:bg-accent rounded" data-testid="conceder-cliente-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!concederResult ? (
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold mb-2">E-mail do cliente</label>
                  <input
                    type="email" autoFocus
                    value={concederForm.email}
                    onChange={(e) => setConcederForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="exemplo@email.com"
                    className="input-field w-full"
                    data-testid="conceder-cliente-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3">Como deseja definir a senha?</label>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${concederForm.metodo === 'manual' ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border hover:border-emerald-300'}`} data-testid="conceder-cliente-metodo-manual">
                      <input type="radio" name="metodo-cli" value="manual" checked={concederForm.metodo === 'manual'} onChange={() => setConcederForm(prev => ({ ...prev, metodo: 'manual' }))} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm"><UserPlus className="w-4 h-4 text-emerald-600" /> Eu defino a senha</div>
                        <p className="text-xs text-muted-foreground mt-0.5">Você cria a senha e passa pessoalmente ao cliente.</p>
                        {concederForm.metodo === 'manual' && (
                          <div className="mt-2 relative">
                            <input
                              type={concederForm.showPassword ? 'text' : 'password'}
                              value={concederForm.senha_personalizada}
                              onChange={(e) => setConcederForm(prev => ({ ...prev, senha_personalizada: e.target.value }))}
                              placeholder="Digite a senha (mín. 4 caracteres)"
                              className="input-field w-full pr-10 text-sm"
                              data-testid="conceder-cliente-senha-manual"
                            />
                            <button type="button" onClick={() => setConcederForm(prev => ({ ...prev, showPassword: !prev.showPassword }))} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded">
                              {concederForm.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        )}
                      </div>
                    </label>

                    <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${concederForm.metodo === 'auto_copiar' ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border hover:border-emerald-300'}`} data-testid="conceder-cliente-metodo-auto-copiar">
                      <input type="radio" name="metodo-cli" value="auto_copiar" checked={concederForm.metodo === 'auto_copiar'} onChange={() => setConcederForm(prev => ({ ...prev, metodo: 'auto_copiar' }))} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm"><Shuffle className="w-4 h-4 text-purple-600" /> Sistema gera senha aleatória — eu copio e envio</div>
                        <p className="text-xs text-muted-foreground mt-0.5">Senha forte gerada; você copia e envia pelo seu canal (WhatsApp, ligação).</p>
                      </div>
                    </label>

                    <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${concederForm.metodo === 'auto_email' ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border hover:border-emerald-300'}`} data-testid="conceder-cliente-metodo-auto-email">
                      <input type="radio" name="metodo-cli" value="auto_email" checked={concederForm.metodo === 'auto_email'} onChange={() => setConcederForm(prev => ({ ...prev, metodo: 'auto_email' }))} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm">
                          <Mail className="w-4 h-4 text-green-600" /> Sistema gera e envia por e-mail
                          <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">Recomendado</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Senha forte enviada automaticamente para o e-mail do cliente.</p>
                      </div>
                    </label>
                  </div>
                </div>

                {concederForm.metodo !== 'manual' ? (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
                    <strong>ℹ️ No primeiro login</strong>, o cliente será obrigado a trocar essa senha por uma pessoal.
                  </div>
                ) : (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-800 dark:text-blue-300">
                    <strong>ℹ️ Senha manual:</strong> o cliente usa essa senha diretamente, sem forçar troca no 1º login.
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 font-bold text-green-700 dark:text-green-400 mb-1">
                    <Check className="w-5 h-5" /> Acesso {concederResult.action} com sucesso!
                  </div>
                  {concederResult.metodo === 'auto_email' && (
                    concederResult.email_enviado ? (
                      <p className="text-sm text-green-700 dark:text-green-400">✉️ E-mail enviado para <b>{concederResult.email}</b>.</p>
                    ) : (
                      <p className="text-sm text-amber-700 dark:text-amber-400">⚠️ E-mail não enviado ({concederResult.erro_email || 'SMTP não configurado'}). Copie e repasse manualmente:</p>
                    )
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">E-MAIL</label>
                    <div className="p-2 bg-muted rounded border border-border"><code className="text-sm">{concederResult.email}</code></div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">SENHA</label>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded border border-border">
                      <code className="flex-1 text-sm font-bold tracking-wider">{concederResult.senha}</code>
                      <button onClick={() => copiarCredenciaisCliente('senha')} className="p-1.5 hover:bg-accent rounded text-emerald-600" title="Copiar apenas a senha" data-testid="conceder-cliente-copiar-senha">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">LINK DE ACESSO</label>
                    <div className="p-2 bg-muted rounded border border-border"><code className="text-xs truncate">{window.location.origin}/login</code></div>
                  </div>
                </div>

                <button onClick={() => copiarCredenciaisCliente('completo')} className="w-full btn-primary py-2.5 flex items-center justify-center gap-2 text-sm font-semibold" data-testid="conceder-cliente-copiar-tudo">
                  <Copy className="w-4 h-4" /> Copiar credenciais completas
                </button>

                {concederResult.must_change_password && (
                  <p className="text-xs text-muted-foreground text-center">ℹ️ No primeiro login, o cliente será obrigado a trocar essa senha.</p>
                )}
              </div>
            )}

            <div className="px-6 py-3 border-t border-border bg-muted/30 flex justify-end gap-2">
              {!concederResult ? (
                <>
                  <button onClick={closeConcederModal} className="px-4 py-2 text-sm rounded hover:bg-accent" data-testid="conceder-cliente-cancelar">Cancelar</button>
                  <button onClick={submitConcederAcesso} disabled={concederLoading} className="btn-primary px-5 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="conceder-cliente-confirmar">
                    {concederLoading ? 'Processando...' : (<><KeyRound className="w-4 h-4" /> Conceder Acesso</>)}
                  </button>
                </>
              ) : (
                <button onClick={closeConcederModal} className="btn-primary px-5 py-2 text-sm font-semibold" data-testid="conceder-cliente-fechar">Concluir</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
